import { useCallback, useMemo } from 'react';

import { useNavigate } from '@tanstack/react-router';

import { stripPageParam, useAdvanceFilters, type UseAdvanceFiltersReturn, useDataTable } from '@/_containers';
import { usePermissions } from '@/_providers';
import { useBulkDelete } from '@/_shared';

import { advFilters } from './components';
import { downloadCsv, toCsv } from './helpers/csv';
import { buildAllInvoicesExportRows } from './helpers/export-rows';
import { invoicesMainListDefaultApiFilter } from './invoices-main-list-default-api-filter';
import { useDefineColumns } from './use-define-columns';

import {
  invalidateQueryAllInvoicesAndBatchObjects,
  type InvoiceQuerySchema,
  useDeleteMutation,
  useGetInvoicesInfinite,
} from '../shared';
import type { InvoiceFilterSchema } from '../shared/filters';
import type { InvoiceEmailPageCallbacks } from '../shared/invoice-email-page-callbacks';

export function useMainInvoicesPage(
  apiCtrl?: UseAdvanceFiltersReturn<InvoiceFilterSchema>,
  emailCallbacks?: InvoiceEmailPageCallbacks
) {
  const localAPICtrl = useAdvanceFilters(advFilters, invoicesMainListDefaultApiFilter);
  const filters = apiCtrl || localAPICtrl;
  const { sorting, allFiltersStr } = filters;

  const infiniteParamStr = useMemo(() => stripPageParam(allFiltersStr), [allFiltersStr]);

  const columns = useDefineColumns(sorting, emailCallbacks);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetInvoicesInfinite(infiniteParamStr);

  const allInvoices = useMemo(() => data.pages.flatMap((p) => p.data), [data.pages]);
  const total = data.pages[0]?.meta?.total ?? 0;

  const table = useDataTable({ columns, data: allInvoices });

  const { currentModelPermissions } = usePermissions();

  const bulkDelete = useBulkDelete({
    deleteMutation: useDeleteMutation(),
    invalidateQueryAllObject: invalidateQueryAllInvoicesAndBatchObjects,
    resetSelection: table.resetRowSelection,
  });

  const nav = useNavigate();
  const handleFocusEnter = useCallback(
    (row: { id: string | number }) => {
      const id = String(row.id);
      nav({ to: `/invoices/$id`, params: { id } });
    },
    [nav]
  );

  const handleOnSingleValueResult = useCallback(() => {
    if (allInvoices.length === 1) {
      nav({
        to: '/invoices/$id',
        params: { id: `${allInvoices[0].id}` },
      });
    }
  }, [allInvoices, nav]);

  const handleExport = useCallback(async (name: string, invoices: InvoiceQuerySchema[]) => {
    if (!invoices) return;

    const rows = buildAllInvoicesExportRows(invoices);
    const csv = toCsv(rows);
    downloadCsv(csv, name + '.csv');
  }, []);

  return {
    ...bulkDelete,
    ...filters,
    table,
    allInvoices,
    total,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    currentModelPermissions,
    handleFocusEnter,
    handleOnSingleValueResult,
    handleExport,
  };
}
